/* eslint-disable @atlaskit/platform/one-value-export-per-file -- This file predates the rule and is a shared subpath entry point consumed by many packages; splitting would require coordinated changes across the entire monorepo. */

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { findFilepaths, isLinkInMatches } from '@atlaskit/editor-common/utils/hyperlink';
import { ADFEncoder } from '@atlaskit/renderer/utils';

import type { EntityAnnotationBatch } from '../content-transformer/entity-annotations';
import { AiMateMarkdownTransformer } from '../content-transformer/md-tokenizer/markdown-transformer';
import {
	ROVO_SKILL_COMBINED_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
	ROVO_SKILL_TAG_PATTERN,
} from '../skills';

export type Content = DocNode | string;

export const isAdfContent = (content: Content): content is DocNode => {
	/*
	This try-catch is a necessary guard rail against broken content to prevent higher level
	error boundaries from displaying the error and breaking the experience.
	*/
	try {
		return typeof content !== 'string' && 'type' in content && content.type === 'doc';
	} catch {
		return false;
	}
};

export const appendContent = (
	content: string | DocNode,
	suggestionContent: string,
): string | DocNode => {
	if (isAdfContent(content)) {
		// Ensure the content is an array of nodes
		const updatedContent = Array.isArray(content.content) ? content.content : [content.content];

		// Append the new paragraph node
		updatedContent.push({
			type: 'paragraph',
			content: [
				{
					type: 'text',
					text: suggestionContent,
				},
			],
		});

		// Return the updated DocNode
		return {
			...content,
			content: updatedContent,
		};
	}
	return `${content}\n\n${suggestionContent}`;
};

const pluginConfig = {
	linkify: {
		findFilepaths,
		isLinkInMatches,
	},
	smartLinks: {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		regexStorage: globalThis.localStorage,
	},
};

// Default schema is https://bitbucket.org/atlassian/adf-schema/src/main/packages/adf-schema/src/schema/default-schema.ts
export const adfEncoder: ADFEncoder<string> = new ADFEncoder(
	(schema) => new AiMateMarkdownTransformer(schema, pluginConfig, false),
);

const streamingAdfEncoder = new ADFEncoder(
	(schema) => new AiMateMarkdownTransformer(schema, pluginConfig, true),
);

export const encodeAdf = (
	content: Content,
	streaming: boolean = false,
	isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean,
	shouldShowAsInline?: boolean,
	useGroupedCitations?: boolean,
	xpcWrapUrl?: (url: string, stripOnly: boolean) => string,
	entityAnnotationBatches?: readonly EntityAnnotationBatch[],
): DocNode => {
	if (isAdfContent(content)) {
		return content;
	}

	if (isExperimentEnabled || shouldShowAsInline || xpcWrapUrl || entityAnnotationBatches?.length) {
		return new ADFEncoder(
			(schema) =>
				new AiMateMarkdownTransformer(
					schema,
					pluginConfig,
					streaming,
					isExperimentEnabled,
					shouldShowAsInline,
					useGroupedCitations,
					xpcWrapUrl,
					entityAnnotationBatches,
				),
		).encode(content);
	} else if (useGroupedCitations) {
		return streaming
			? new ADFEncoder(
					(schema) =>
						new AiMateMarkdownTransformer(schema, pluginConfig, true, undefined, undefined, true),
				).encode(content)
			: new ADFEncoder(
					(schema) =>
						new AiMateMarkdownTransformer(
							schema,
							pluginConfig,
							streaming,
							isExperimentEnabled,
							shouldShowAsInline,
							useGroupedCitations,
						),
				).encode(content);
	} else {
		return streaming ? streamingAdfEncoder.encode(content) : adfEncoder.encode(content);
	}
};

export const encodeSkillPromptToAdf = (content: string): { adf: DocNode; promptLength: number } => {
	const contentNodes: Array<
		| { type: 'text'; text: string }
		| {
				type: 'inlineExtension';
				attrs: { extensionKey: string; extensionType: string; parameters: { slug: string } };
		  }
	> = [];
	let lastIndex = 0;
	let match;
	let promptLength = 0;

	// Find all skill tag matches
	const matches: Array<{ index: number; skillSlug: string; length: number }> = [];
	while ((match = ROVO_SKILL_TAG_PATTERN.exec(content)) !== null) {
		matches.push({
			index: match.index,
			skillSlug: match[1], // The skill name without the slash
			length: match[0].length, // Length of "/skill-name"
		});
	}

	// Process the string segment by segment
	for (let i = 0; i < matches.length; i++) {
		const currentMatch = matches[i];
		const matchStart = currentMatch.index;
		const matchEnd = matchStart + currentMatch.length;

		// Add text before the skill tag (if any)
		if (matchStart > lastIndex) {
			const textBefore = content.substring(lastIndex, matchStart);
			if (textBefore) {
				contentNodes.push({
					type: 'text',
					text: textBefore,
				});
				promptLength += textBefore.length;
			}
		}

		// Add the skill tag as inlineExtension
		contentNodes.push({
			type: 'inlineExtension',
			attrs: {
				extensionKey: ROVO_SKILL_COMBINED_EXTENSION_KEY,
				extensionType: ROVO_SKILL_EXTENSION_TYPE,
				parameters: {
					slug: currentMatch.skillSlug,
				},
			},
		});
		promptLength += 1;

		lastIndex = matchEnd;
	}

	// Add remaining text after the last match (if any)
	if (lastIndex < content.length) {
		const textAfter = content.substring(lastIndex);
		if (textAfter) {
			contentNodes.push({
				type: 'text',
				text: textAfter,
			});
			promptLength += textAfter.length;
		}
	}

	// If no matches were found, return the entire string as text
	if (contentNodes.length === 0) {
		contentNodes.push({
			type: 'text',
			text: content,
		});
		promptLength += content.length;
	}

	return {
		adf: {
			type: 'doc',
			version: 1,
			content: [
				{
					type: 'paragraph',
					content: contentNodes,
				},
			],
		},
		promptLength,
	};
};

export const PROMPT_PLACEHOLDER_CHAR = '`';

export type QuotedSkillTagsAdfResult = {
	adf: DocNode;
	promptLength: number;
	placeholderRange?: { start: number; end: number; label: string };
};

type ContentNode =
	| { type: 'text'; text: string }
	| {
			type: 'inlineExtension';
			attrs: {
				extensionKey: string;
				extensionType: string;
				parameters: { slug: string; [key: string]: unknown };
			};
	  };

type DetectedSkill = { index: number; slug: string; length: number };

// encodeAdf removes leading spaces, so prepend a text node with the segment's leading spaces to preserve them.
const preserveLeadingSpaces = (textSegment: string, nodes: ContentNode[]): ContentNode[] => {
	const leading = textSegment.match(/^\s*/)?.[0] ?? '';
	if (leading.length === 0) {
		return nodes;
	}
	return [{ type: 'text', text: leading }, ...nodes];
};

// Collect all content nodes from all top-level blocks, since encodeAdf can return multiple paragraphs for e.g. "line1\n\nline2".
const getAllNodesFromSegmentAdf = (segmentAdf: DocNode, fallbackSegment: string): ContentNode[] => {
	const blocks = segmentAdf.content ?? [];
	if (blocks.length === 0) {
		return [{ type: 'text' as const, text: fallbackSegment }];
	}
	const nodes = blocks.flatMap((block) => (block as { content?: ContentNode[] }).content ?? []);
	return nodes.length > 0 ? nodes : [{ type: 'text' as const, text: fallbackSegment }];
};

const detectSkills = (
	content: string,
	skillMetadata?: Record<string, Record<string, unknown>>,
): DetectedSkill[] => {
	if (!skillMetadata) {
		return [];
	}
	const regex = new RegExp(ROVO_SKILL_TAG_PATTERN.source, 'g');
	const matches = [...content.matchAll(regex)];
	if (matches.length === 0) {
		return [];
	}
	return matches
		.filter((m) => m[1] in skillMetadata)
		.map((m) => ({
			index: m.index ?? 0,
			slug: m[1],
			length: m[0].length,
		}));
};

// The conversation input editor only supports a limited set of block node types.
// Convert unsupported block nodes to paragraph nodes so that prompts containing
// markdown headings (e.g. "## Title") or horizontal rules (e.g. "---") do not
// trigger "This editor does not support displaying this content: <type>" when inserted.
const sanitizeAdfForPromptInput = (adf: DocNode): DocNode => {
	if (!adf.content) {
		return adf;
	}
	return {
		...adf,
		content: adf.content.flatMap((node) => {
			if (node.type === 'heading') {
				// Convert heading to paragraph, preserving inner text content
				return [{ type: 'paragraph', content: node.content }];
			}
			if (node.type === 'rule') {
				// Drop horizontal rules entirely — they have no content to preserve
				return [];
			}
			return [node];
		}),
	};
};

export const encodePromptToAdf = (
	content: string,
	skillMetadata?: Record<string, Record<string, unknown>>,
	shouldShowAsInline?: boolean,
): QuotedSkillTagsAdfResult => {
	const skillMatches = detectSkills(content, skillMetadata);
	if (skillMatches.length === 0) {
		const { placeholderRange } = getPlaceholderRange(content, 0);
		return {
			adf: sanitizeAdfForPromptInput(encodeAdf(content, false, undefined, shouldShowAsInline)),
			promptLength: content.length,
			...(placeholderRange && { placeholderRange }),
		};
	}

	return encodePromptWithSkills(content, skillMatches, skillMetadata);
};

const encodePromptWithSkills = (
	content: string,
	skillMatches: DetectedSkill[],
	skillMetadata?: Record<string, Record<string, unknown>>,
): QuotedSkillTagsAdfResult => {
	const contentNodes: ContentNode[] = [];
	let lastIndex = 0;
	let promptLength = 0;
	let placeholderRange: QuotedSkillTagsAdfResult['placeholderRange'];

	for (let i = 0; i < skillMatches.length; i++) {
		const currentMatch = skillMatches[i];
		const matchStart = currentMatch.index;
		const matchEnd = matchStart + currentMatch.length;

		// Add text and placeholder range before the skill tag (if any)
		if (matchStart > lastIndex) {
			const textSegment = content.substring(lastIndex, matchStart);
			if (textSegment) {
				const { placeholderRange: range } = getPlaceholderRange(textSegment, promptLength);
				if (range) {
					placeholderRange = range;
				}
				const segmentAdf = encodeAdf(textSegment);
				const nodes = getAllNodesFromSegmentAdf(segmentAdf, textSegment);
				contentNodes.push(...preserveLeadingSpaces(textSegment, nodes));
				promptLength += textSegment.length;
			}
		}

		// Add the skill tag as inlineExtension
		contentNodes.push({
			type: 'inlineExtension',
			attrs: {
				extensionKey: ROVO_SKILL_COMBINED_EXTENSION_KEY,
				extensionType: ROVO_SKILL_EXTENSION_TYPE,
				parameters: {
					slug: currentMatch.slug,
					...skillMetadata?.[currentMatch.slug],
				},
			},
		});
		promptLength += 1;
		lastIndex = matchEnd;
	}

	// Add remaining text and placeholder range after the last match (if any)
	if (lastIndex < content.length) {
		const textSegment = content.substring(lastIndex);
		if (textSegment) {
			const { placeholderRange: range } = getPlaceholderRange(textSegment, promptLength);
			if (range) {
				placeholderRange = range;
			}
			const segmentAdf = encodeAdf(textSegment);
			const nodes = getAllNodesFromSegmentAdf(segmentAdf, textSegment);
			contentNodes.push(...preserveLeadingSpaces(textSegment, nodes));
			promptLength += textSegment.length;
		}
	}
	return {
		adf: {
			type: 'doc',
			version: 1,
			content: [{ type: 'paragraph', content: contentNodes }],
		},
		promptLength,
		...(placeholderRange && { placeholderRange }),
	};
};

// Returns the placeholder range for link typeahead when content has `label` backticks.
const getPlaceholderRange = (
	segment: string,
	currentContentIndex: number,
): { placeholderRange?: { start: number; end: number; label: string } } => {
	const firstBacktick = segment.indexOf(PROMPT_PLACEHOLDER_CHAR);
	const lastBacktick = segment.lastIndexOf(PROMPT_PLACEHOLDER_CHAR);
	if (firstBacktick !== -1 && lastBacktick !== -1 && firstBacktick !== lastBacktick) {
		const label = segment.slice(firstBacktick + 1, lastBacktick);
		const start = currentContentIndex + firstBacktick + 1;
		return {
			placeholderRange: { start, end: start + label.length, label },
		};
	}
	return {};
};
